import { useEffect, useMemo, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { MessageSquare, X, Send, Sparkles } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;
const SESSION_KEY = "rob4nh_chat_session";

/* ---------------------------------------------------------------- */
/*  Session id: stable per-browser so history persists across pages  */
/* ---------------------------------------------------------------- */
const getSessionId = () => {
  if (typeof window === "undefined") return "server";
  let id = window.localStorage.getItem(SESSION_KEY);
  if (!id) {
    id = `s_${Math.random().toString(36).slice(2, 10)}${Date.now().toString(36)}`;
    window.localStorage.setItem(SESSION_KEY, id);
  }
  return id;
};

const SUGGESTIONS = [
  "Where do I vote?",
  "What's Rob's housing plan?",
  "How can I volunteer?",
  "Explain the floterial district",
];

/* ---------------------------------------------------------------- */
/*  Streaming client — reads SSE from /api/chat/message              */
/* ---------------------------------------------------------------- */
async function streamChat({ sessionId, message, onDelta, onDone, onError, signal }) {
  const res = await fetch(`${API}/api/chat/message`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ session_id: sessionId, message }),
    signal,
  });
  if (!res.ok || !res.body) {
    onError?.(`HTTP ${res.status}`);
    return;
  }
  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    // Split on the SSE frame terminator
    const frames = buffer.split("\n\n");
    buffer = frames.pop() || "";
    for (const frame of frames) {
      const line = frame.trim();
      if (!line.startsWith("data:")) continue;
      const jsonStr = line.replace(/^data:\s*/, "");
      try {
        const evt = JSON.parse(jsonStr);
        if (evt.delta) onDelta?.(evt.delta);
        if (evt.error) onError?.(evt.error);
        if (evt.done) onDone?.();
      } catch {
        /* ignore malformed frame */
      }
    }
  }
  onDone?.();
}

/* ---------------------------------------------------------------- */
/*  Component                                                        */
/* ---------------------------------------------------------------- */
export const ChatBot = () => {
  const [open, setOpen] = useState(false);
  const [input, setInput] = useState("");
  const [messages, setMessages] = useState([]);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState(null);
  const controllerRef = useRef(null);
  const listRef = useRef(null);
  const sessionId = useMemo(getSessionId, []);

  // Auto-scroll to bottom on new content
  useEffect(() => {
    const el = listRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages, open]);

  // Escape closes
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  const send = async (text) => {
    const trimmed = (text ?? input).trim();
    if (!trimmed || pending) return;
    setError(null);
    setInput("");
    setMessages((m) => [
      ...m,
      { role: "user", content: trimmed },
      { role: "assistant", content: "" },
    ]);
    setPending(true);

    const controller = new AbortController();
    controllerRef.current = controller;
    await streamChat({
      sessionId,
      message: trimmed,
      signal: controller.signal,
      onDelta: (delta) => {
        setMessages((m) => {
          const copy = [...m];
          const last = copy[copy.length - 1];
          if (last && last.role === "assistant") {
            copy[copy.length - 1] = { ...last, content: last.content + delta };
          }
          return copy;
        });
      },
      onError: (err) => setError(String(err)),
      onDone: () => setPending(false),
    });
    setPending(false);
  };

  const onSubmit = (e) => {
    e.preventDefault();
    send();
  };

  return (
    <>
      {/* Trigger */}
      <motion.button
        type="button"
        onClick={() => setOpen((o) => !o)}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 1.2, duration: 0.6, ease: [0.19, 1, 0.22, 1] }}
        whileHover={{ scale: 1.04 }}
        whileTap={{ scale: 0.97 }}
        className="fixed bottom-6 right-6 z-40 group inline-flex items-center gap-2 rounded-full bg-terracotta text-graphite pl-3 pr-5 py-3 font-semibold uppercase tracking-[0.16em] text-[11px] shadow-[0_16px_40px_-12px_rgba(245,138,63,0.65)] hover:bg-cream transition-colors"
        data-testid="chatbot-toggle"
        aria-label={open ? "Close campaign assistant" : "Open campaign assistant"}
      >
        <span className="grid h-7 w-7 place-items-center rounded-full bg-graphite text-terracotta">
          {open ? <X size={14} /> : <Sparkles size={14} />}
        </span>
        <span className="hidden sm:inline">
          {open ? "Close" : "Ask the campaign"}
        </span>
      </motion.button>

      {/* Panel */}
      <AnimatePresence>
        {open && (
          <motion.div
            key="chat-panel"
            initial={{ opacity: 0, y: 24, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 24, scale: 0.98 }}
            transition={{ duration: 0.35, ease: [0.19, 1, 0.22, 1] }}
            className="fixed bottom-24 right-4 sm:right-6 z-40 w-[calc(100vw-2rem)] sm:w-[420px] max-h-[calc(100vh-8rem)] flex flex-col rounded-3xl overflow-hidden glass-strong"
            data-testid="chatbot-panel"
          >
            {/* Header */}
            <div className="relative bg-graphite/80 backdrop-blur-xl px-5 py-4 border-b border-white/10">
              <div
                className="absolute inset-0 -z-10 pointer-events-none"
                style={{
                  background:
                    "radial-gradient(60% 55% at 100% 0%, rgba(245,138,63,0.28) 0%, rgba(3,7,17,0) 70%)",
                }}
              />
              <div className="flex items-center gap-3">
                <span className="relative flex h-2.5 w-2.5">
                  <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-terracotta opacity-70" />
                  <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-terracotta" />
                </span>
                <div>
                  <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-terracotta">
                    Ask the campaign
                  </p>
                  <p className="text-cream text-sm mt-0.5">
                    Voter assistant · Hillsborough 40
                  </p>
                </div>
              </div>
            </div>

            {/* Body */}
            <div
              ref={listRef}
              className="flex-1 overflow-y-auto px-5 py-5 space-y-4 bg-canvas/70 backdrop-blur-xl"
              data-testid="chatbot-messages"
            >
              {messages.length === 0 && (
                <div>
                  <p className="text-ink/85 leading-relaxed">
                    Hi — I&apos;m the campaign&apos;s voter assistant. Ask me about Rob&apos;s
                    platform, your polling place, or how to get involved.
                  </p>
                  <div className="mt-4 flex flex-wrap gap-2">
                    {SUGGESTIONS.map((s) => (
                      <button
                        key={s}
                        type="button"
                        onClick={() => send(s)}
                        disabled={pending}
                        data-testid={`chatbot-suggestion-${s.slice(0, 12).replace(/\s+/g, "-").toLowerCase()}`}
                        className="text-left text-xs px-3 py-2 rounded-full border border-white/10 bg-white/[0.04] text-ink/85 hover:bg-white/[0.08] hover:border-terracotta/40 transition-colors"
                      >
                        {s}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {messages.map((m, i) => (
                <div
                  key={i}
                  className={
                    m.role === "user"
                      ? "flex justify-end"
                      : "flex justify-start"
                  }
                >
                  <div
                    className={
                      m.role === "user"
                        ? "max-w-[85%] rounded-2xl rounded-br-md bg-terracotta text-graphite px-4 py-2.5 text-sm leading-relaxed shadow-[0_8px_20px_-10px_rgba(245,138,63,0.6)]"
                        : "max-w-[90%] rounded-2xl rounded-bl-md bg-white/[0.05] border border-white/10 text-ink px-4 py-2.5 text-sm leading-relaxed whitespace-pre-wrap"
                    }
                    data-testid={`chatbot-msg-${m.role}-${i}`}
                  >
                    {m.role === "assistant" && !m.content && pending ? (
                      <span className="inline-flex gap-1 items-center">
                        <span className="h-1.5 w-1.5 bg-terracotta rounded-full animate-bounce" style={{ animationDelay: "0ms" }} />
                        <span className="h-1.5 w-1.5 bg-terracotta rounded-full animate-bounce" style={{ animationDelay: "120ms" }} />
                        <span className="h-1.5 w-1.5 bg-terracotta rounded-full animate-bounce" style={{ animationDelay: "240ms" }} />
                      </span>
                    ) : (
                      m.content
                    )}
                  </div>
                </div>
              ))}

              {error && (
                <div className="text-xs text-terracotta/90 border border-terracotta/30 bg-terracotta/10 rounded-xl px-3 py-2">
                  Something went wrong: {error}. Please try again or email{" "}
                  <a href="mailto:rob4nh@gmail.com" className="underline">
                    rob4nh@gmail.com
                  </a>.
                </div>
              )}
            </div>

            {/* Composer */}
            <form
              onSubmit={onSubmit}
              className="px-4 py-3 border-t border-white/10 bg-graphite/70 backdrop-blur-xl flex items-center gap-2"
              data-testid="chatbot-composer"
            >
              <input
                type="text"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Ask about voting, issues, or volunteering…"
                disabled={pending}
                autoFocus
                className="flex-1 bg-white/[0.05] border border-white/10 rounded-full px-4 py-2.5 text-sm text-ink placeholder:text-ink/40 focus:outline-none focus:border-terracotta/60 focus:bg-white/[0.08] transition-colors disabled:opacity-60"
                data-testid="chatbot-input"
              />
              <button
                type="submit"
                disabled={pending || !input.trim()}
                className="grid h-10 w-10 place-items-center rounded-full bg-terracotta text-graphite hover:bg-cream disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                data-testid="chatbot-send"
                aria-label="Send message"
              >
                <Send size={16} />
              </button>
            </form>

            {/* Footnote */}
            <div className="px-4 py-2 text-[10px] font-mono uppercase tracking-[0.28em] text-ink/40 bg-graphite/70 border-t border-white/5">
              <MessageSquare size={10} className="inline mr-2 -mt-0.5" />
              Answers grounded in Rob&apos;s published platform
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default ChatBot;
